import { SearchEventType } from "globals/types/models.ts";
import { useAppDispatch, useAppSelector } from "config/store";
import { adminActions, AdminPushLogType, PushRecipientType, PushStatsType } from "config/store/modules/admin.ts";
import { globalActions } from "config/store/modules/global.ts";
import { useCallback, useEffect, useMemo, useState } from "react";
import AdminConnectedContainer from "components/container/AdminConnectedContainer.tsx";
import Button from "components/atom/button/Button.tsx";
import Input from "components/atom/Input.tsx";
import Icon from "components/atom/Icon.tsx";
import { useTranslation } from "react-i18next";
import { LANGS } from "globals/constants.ts";
import dayjs from "dayjs";
import AroundMapPicker, { LatLng } from "components/admin/AroundMapPicker.tsx";

const MAX_LENGTH = 178;

//diameter options (in km) offered for the "around a point" audience
const DIAMETER_OPTIONS = [5, 10, 25, 50, 100, 250, 500, 750, 1000];

// Outline buttons that fill when selected (replaces the tabbed buttons)
function SelectButtons<T extends string>(props: {
  value: T,
  options: { label: string, value: T }[],
  onChange: (value: T) => void
}) {
  return (
    <div className="flex flex-row flex-wrap gap-2">
      {props.options.map(opt => {
        const selected = props.value === opt.value;
        return (
          <button
            type="button"
            key={opt.value}
            onClick={() => props.onChange(opt.value)}
            className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${selected ? "border-green-500 bg-green-500 text-black" : "border-gray-300 bg-white text-gray-700 hover:border-gray-500"}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function AdminNotificationsPage() {

  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const currentLang = useAppSelector(state => state.global.currentLang);
  const recipients: PushRecipientType[] = useAppSelector(state => state.admin.pushRecipients);
  const events: SearchEventType[] = useAppSelector(state => state.admin.events);
  const pushLogs: AdminPushLogType[] = useAppSelector(state => state.admin.pushLogs);
  const pushStats: PushStatsType = useAppSelector(state => state.admin.pushStats);

  const [titleFr, setTitleFr] = useState<string>("");
  const [titleEn, setTitleEn] = useState<string>("");
  const [bodyFr, setBodyFr] = useState<string>("");
  const [bodyEn, setBodyEn] = useState<string>("");
  const [priority, setPriority] = useState<"normal" | "high">("normal");
  const [audience, setAudience] = useState<"all" | "specific" | "around">("all");
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedTokenIds, setSelectedTokenIds] = useState<number[]>([]);
  const [eventSearch, setEventSearch] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState<SearchEventType | null>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [confirming, setConfirming] = useState<boolean>(false);
  const [aroundCenter, setAroundCenter] = useState<LatLng | null>(null);
  const [aroundDiameterKm, setAroundDiameterKm] = useState<number>(25);
  const [aroundLoading, setAroundLoading] = useState<boolean>(false);

  //load recent logs, reachable recipients and a first page of events on mount
  useEffect(() => {
    dispatch(adminActions.getPushLogs());
    dispatch(adminActions.getPushRecipients());
    dispatch(adminActions.getEvents());
  }, [dispatch]);

  const getEventTitle = useCallback((event: SearchEventType) => {
    return (currentLang === LANGS.FR ? event.titleFr : event.titleEn || event.titleFr) || `#${event.id}`;
  }, [currentLang]);

  //one row per active push token; a token may be linked to a user or be anonymous (sessionId only)
  const filteredRecipients = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) {
      return recipients;
    }
    return recipients.filter(recipient => {
      return `${recipient.id}`.includes(search)
        || (recipient.userId != null && `${recipient.userId}`.includes(search))
        || (recipient.name || "").toLowerCase().includes(search)
        || (recipient.email || "").toLowerCase().includes(search)
        || (recipient.sessionId || "").toLowerCase().includes(search);
    });
  }, [recipients, searchValue]);

  const toggleToken = useCallback((tokenId: number) => {
    setSelectedTokenIds(prev => prev.includes(tokenId)
      ? prev.filter(id => id !== tokenId)
      : [...prev, tokenId]);
  }, []);

  const searchEvents = useCallback(() => {
    dispatch(adminActions.getEvents(eventSearch));
  }, [dispatch, eventSearch]);

  //when an event is attached, use its coordinates as the "around" center
  useEffect(() => {
    if (selectedEvent && selectedEvent.latitude != null && selectedEvent.longitude != null) {
      setAroundCenter({ lat: Number(selectedEvent.latitude), lng: Number(selectedEvent.longitude) });
    }
  }, [selectedEvent]);

  //ask the backend which reachable users have their last known location within the area
  const selectAround = useCallback(async () => {
    if (!aroundCenter || aroundLoading) {
      return;
    }
    setAroundLoading(true);
    try {
      const res = await dispatch(adminActions.getRecipientsAround(aroundCenter.lat, aroundCenter.lng, aroundDiameterKm / 2));
      const tokenIds = res?.pushTokenIds ?? [];
      setSelectedTokenIds(tokenIds);
      dispatch(globalActions.setToast({
        type: tokenIds.length > 0 ? "success" : "warning",
        message: tokenIds.length > 0
          ? t("admin.notifications.around.selectedToast", { count: tokenIds.length })
          : t("admin.notifications.around.noneToast")
      }));
    } catch (err) {
      dispatch(globalActions.setToast({
        type: "error",
        message: t("admin.notifications.around.errorToast")
      }));
    } finally {
      setAroundLoading(false);
    }
  }, [aroundCenter, aroundDiameterKm, aroundLoading, dispatch, t]);

  const canSend = useMemo(() => {
    if (!titleFr.trim() || !bodyFr.trim()) {
      return false;
    }
    if (audience !== "all" && selectedTokenIds.length === 0) {
      return false;
    }
    return !isSending;
  }, [titleFr, bodyFr, audience, selectedTokenIds, isSending]);

  const onSend = useCallback(async () => {
    if (!canSend) {
      return;
    }
    setIsSending(true);
    const tokenIds = audience === "all" ? [] : selectedTokenIds;
    try {
      const res = await dispatch(adminActions.sendPushNotification(titleFr.trim(), titleEn.trim(), bodyFr.trim(), bodyEn.trim(), tokenIds, priority, selectedEvent?.id ?? null));
      const result = res?.result;
      dispatch(globalActions.setToast({
        type: "success",
        message: t("admin.notifications.sentToast", {
          sent: result?.sent ?? 0,
          total: result?.total ?? 0
        })
      }));
      //reset the message and refresh the logs
      setTitleFr("");
      setTitleEn("");
      setBodyFr("");
      setBodyEn("");
      setSelectedTokenIds([]);
      setSelectedEvent(null);
      setConfirming(false);
      dispatch(adminActions.getPushLogs());
    } catch (err: any) {
      const apiError = err?.response?.data?.errors?.global;
      dispatch(globalActions.setToast({
        type: "error",
        message: apiError || t("admin.notifications.errorToast")
      }));
      setConfirming(false);
    } finally {
      setIsSending(false);
    }
  }, [canSend, audience, selectedTokenIds, dispatch, titleFr, titleEn, bodyFr, bodyEn, priority, selectedEvent, t]);

  const confirmCount = audience === "all" ? pushStats.activeTokenCount : selectedTokenIds.length;

  return (
    <AdminConnectedContainer title={t("menu.admin.notifications")}>
      <div className="w-full space-y-8">

        {pushStats && (
          <div className="flex flex-row flex-wrap items-center justify-start gap-4">
            <div className="rounded-md border border-gray-200 px-4 py-2">
              <p className="text-xs text-gray-500">{t("admin.notifications.stats.totalUsers")}</p>
              <p className="text-lg font-medium text-black">{pushStats.totalUsers}</p>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-2">
              <p className="text-xs text-gray-500">{t("admin.notifications.stats.usersWithApp")}</p>
              <p className="text-lg font-medium text-black">{pushStats.usersWithPushToken}</p>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-2">
              <p className="text-xs text-gray-500">{t("admin.notifications.stats.activeDevices")}</p>
              <p className="text-lg font-medium text-black">{pushStats.activeTokenCount}</p>
            </div>
            <div className="rounded-md border border-gray-200 px-4 py-2">
              <p className="text-xs text-gray-500">{t("admin.notifications.stats.downloadRate")}</p>
              <p className="text-lg font-medium text-black">{Math.round(pushStats.downloadRate * 100)}%</p>
            </div>
          </div>
        )}

        <div className="w-full space-y-4 rounded-md border border-gray-200 p-4">
          <h3 className="text-lg font-medium">{t("admin.notifications.composeTitle")}</h3>

          <Input
            name="titleFr"
            label={t("admin.notifications.fields.titleFr")}
            formikField={false}
            value={titleFr}
            onChange={(e) => setTitleFr(e.target.value.slice(0, MAX_LENGTH))}
          />

          <div className="c__input">
            <label className="block text-sm font-medium" htmlFor="push-body-fr">
              {t("admin.notifications.fields.bodyFr")}
            </label>
            <textarea
              id="push-body-fr"
              className="mt-1 h-28 w-full resize-none rounded-md border border-gray-300 p-2 text-black"
              value={bodyFr}
              maxLength={MAX_LENGTH}
              onChange={(e) => setBodyFr(e.target.value)}
            />
            <p className="text-right text-xs text-gray-400">{bodyFr.length}/{MAX_LENGTH}</p>
          </div>

          <Input
            name="titleEn"
            label={t("admin.notifications.fields.titleEn")}
            formikField={false}
            value={titleEn}
            onChange={(e) => setTitleEn(e.target.value.slice(0, MAX_LENGTH))}
          />

          <div className="c__input">
            <label className="block text-sm font-medium" htmlFor="push-body-en">
              {t("admin.notifications.fields.bodyEn")}
            </label>
            <textarea
              id="push-body-en"
              className="mt-1 h-28 w-full resize-none rounded-md border border-gray-300 p-2 text-black"
              value={bodyEn}
              maxLength={MAX_LENGTH}
              onChange={(e) => setBodyEn(e.target.value)}
            />
            <p className="text-right text-xs text-gray-400">{bodyEn.length}/{MAX_LENGTH}</p>
          </div>

          <div>
            <p className="mb-1 text-sm font-medium">{t("admin.notifications.fields.priority")}</p>
            <SelectButtons
              value={priority}
              onChange={(value) => setPriority(value)}
              options={[
                { label: t("admin.notifications.priority.normal"), value: "normal" },
                { label: t("admin.notifications.priority.high"), value: "high" }
              ]}
            />
          </div>

          <div>
            <p className="mb-1 text-sm font-medium">{t("admin.notifications.fields.audience")}</p>
            <SelectButtons
              value={audience}
              onChange={(value) => setAudience(value)}
              options={[
                { label: t("admin.notifications.audience.all"), value: "all" },
                { label: t("admin.notifications.audience.specific"), value: "specific" },
                { label: t("admin.notifications.audience.around"), value: "around" }
              ]}
            />
          </div>

          {audience === "around" && (
            <div className="space-y-3 rounded-md border border-gray-200 p-3">
              <p className="text-sm text-gray-600">{t("admin.notifications.around.instructions")}</p>

              <AroundMapPicker
                center={aroundCenter}
                diameterKm={aroundDiameterKm}
                onCenterChange={setAroundCenter}
              />

              <p className="text-xs text-gray-500">
                {aroundCenter
                  ? t("admin.notifications.around.center", {
                    lat: aroundCenter.lat.toFixed(5),
                    lng: aroundCenter.lng.toFixed(5)
                  }) + (selectedEvent ? ` · ${t("admin.notifications.around.eventCenterHint")}` : "")
                  : t("admin.notifications.around.noCenter")}
              </p>

              <div>
                <p className="mb-1 text-sm font-medium">{t("admin.notifications.around.diameter")}</p>
                <SelectButtons
                  value={`${aroundDiameterKm}`}
                  onChange={(value) => setAroundDiameterKm(Number(value))}
                  options={DIAMETER_OPTIONS.map(km => ({ label: `${km} km`, value: `${km}` }))}
                />
              </div>

              <Button
                style="border"
                small
                disabled={!aroundCenter || aroundLoading}
                onClick={selectAround}
              >
                {aroundLoading ? t("admin.notifications.around.selecting") : t("admin.notifications.around.selectUsers")}
              </Button>
            </div>
          )}

          {audience !== "all" && (
            <div className="space-y-2">
              <Input
                name="userSearch"
                label={t("admin.notifications.searchUsers")}
                formikField={false}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                {t("admin.notifications.selectedCount", { count: selectedTokenIds.length })}
              </p>
              <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                {filteredRecipients.map(recipient => (
                  <button
                    type="button"
                    key={recipient.id}
                    onClick={() => toggleToken(recipient.id)}
                    className={`flex w-full flex-row items-center justify-between gap-2 border-b border-gray-100 px-3 py-2 text-left ${selectedTokenIds.includes(recipient.id) ? "bg-green-150" : "bg-white"}`}
                  >
                    <span className="text-sm text-black">
                      {recipient.userId != null
                        ? `#${recipient.userId} — (${recipient.email})`
                        : t("admin.notifications.anonymousRecipient", { session: (recipient.sessionId || "").slice(0, 8) || recipient.id })}
                      <span className="ml-1 text-xs text-gray-400">· {recipient.type}</span>
                      <span className="ml-1 text-xs text-gray-400">· {recipient.deviceName}</span>
                    </span>
                    {selectedTokenIds.includes(recipient.id) && (
                      <Icon name="check" className="h-4 w-4 text-green-500" />
                    )}
                  </button>
                ))}
                {filteredRecipients.length === 0 && (
                  <p className="px-3 py-2 text-sm text-gray-400">{t("admin.notifications.noUsers")}</p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <p className="text-sm font-medium">{t("admin.notifications.event.label")}</p>
            {selectedEvent ? (
              <div className="flex flex-row items-center justify-between gap-3 rounded-md border border-green-500 bg-green-150 p-2">
                <div className="flex flex-row items-center gap-3">
                  {selectedEvent.images?.[0]?.imageUrl && (
                    <img src={selectedEvent.images[0].imageUrl} alt="" className="h-12 w-12 rounded-md object-cover" />
                  )}
                  <div>
                    <p className="text-sm text-black">{getEventTitle(selectedEvent)}</p>
                    <p className="text-xs text-gray-500">{t("admin.notifications.event.imageIncluded")}</p>
                  </div>
                </div>
                <button type="button" className="text-sm underline" onClick={() => setSelectedEvent(null)}>
                  {t("admin.notifications.event.clear")}
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-row items-center justify-center gap-4"
                     onKeyUp={(e) => {
                       if (e.key === "Enter") {
                         searchEvents();
                       }
                     }}>
                  <Input
                    name="eventSearch"
                    className="flex-1"
                    placeholder={t("admin.notifications.event.searchPlaceholder")}
                    formikField={false}
                    value={eventSearch}
                    onChange={(e) => setEventSearch(e.target.value)}
                  />
                  <button className="h-10 w-12 flex flex-row items-center justify-center rounded-md bg-black"
                          type="button" onClick={searchEvents}>
                    <Icon name="search" className="block h-4 w-4 min-w-4 text-white" />
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200">
                  {events.map(event => (
                    <button
                      type="button"
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className="flex w-full flex-row items-center gap-3 border-b border-gray-100 bg-white px-3 py-2 text-left hover:bg-gray-50"
                    >
                      {event.images?.[0]?.imageUrl ? (
                        <img src={event.images[0].imageUrl} alt="" className="h-10 w-10 rounded-md object-cover" />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-gray-100">
                          <Icon name="events" className="h-4 w-4 text-gray-400" />
                        </div>
                      )}
                      <span className="text-sm text-black">#{event.id} — {getEventTitle(event)}</span>
                    </button>
                  ))}
                  {events.length === 0 && (
                    <p className="px-3 py-2 text-sm text-gray-400">{t("admin.notifications.event.noEvents")}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {confirming && (
            <div className="flex flex-col gap-1 rounded-md border border-orange-600 bg-gray-50 p-3">
              <div className="flex flex-row items-center gap-2">
                <Icon name="warning" className="h-5 w-5 text-orange-600" />
                <p className="text-sm font-medium text-black">{t("admin.notifications.confirm.title")}</p>
              </div>
              <p className="text-sm text-gray-700">
                {t("admin.notifications.confirm.message", { count: confirmCount })}
              </p>
            </div>
          )}

          <div className="flex flex-row justify-end gap-3">
            {confirming ? (
              <>
                <Button style="border" disabled={isSending} onClick={() => setConfirming(false)}>
                  {t("cancel")}
                </Button>
                <Button disabled={isSending} onClick={onSend}>
                  {isSending ? t("admin.notifications.sending") : t("admin.notifications.confirm.send")}
                </Button>
              </>
            ) : (
              <Button disabled={!canSend} onClick={() => setConfirming(true)}>
                {t("admin.notifications.send")}
              </Button>
            )}
          </div>
        </div>

        <div className="w-full space-y-2">
          <h3 className="text-lg font-medium">{t("admin.notifications.historyTitle")}</h3>
          <div className="flex flex-row items-center justify-start gap-4 border-b border-gray-300 pb-1">
            <div className="w-[60px]"><p className="text-sm font-medium text-black">ID</p></div>
            <div className="w-2/12"><p
              className="text-sm font-medium text-black">{t("admin.notifications.fields.title")}</p></div>
            <div className="w-3/12"><p
              className="text-sm font-medium text-black">{t("admin.notifications.fields.body")}</p></div>
            <div className="w-3/12"><p
              className="text-sm font-medium text-black">{t("admin.notifications.table.user")}</p></div>
            <div className="w-1/12"><p
              className="text-sm font-medium text-black">{t("admin.notifications.table.status")}</p></div>
            <div className="w-2/12"><p
              className="text-sm font-medium text-black">{t("admin.notifications.table.sentAt")}</p></div>
          </div>

          {pushLogs.map(log => (
            <div
              className={`flex flex-row items-center justify-start gap-4 py-1 ${log.status === "error" ? "bg-red-100" : "bg-gray-50"}`}
              key={log.id}>
              <div className="w-[60px] pl-1"><p className="text-black">#{log.id}</p></div>
              <div className="w-2/12"><p className="truncate text-black">{log.title}</p></div>
              <div className="w-3/12"><p className="truncate text-black">{log.body}</p></div>
              <div className="w-2/12">
                <p className="truncate text-black">
                  {log.user ? `${log.user.name} (${log.user.email})` : "—"}
                </p>
              </div>
              <div className="w-1/12">
                <p className={log.status === "error" ? "text-red-500" : "text-green-600"}>
                  {log.status === "error" ? (log.errorCode || t("admin.notifications.table.failed")) : t("admin.notifications.table.ok")}
                </p>
              </div>
              <div className="w-1/12">
                <p className="">
                  {log.opened ? (t("admin.notifications.opened")) : t("admin.notifications.notOpened")}
                </p>
              </div>
              <div className="w-2/12">
                <p className="text-black">{log.createdAt ? dayjs(log.createdAt).format("D MMM YYYY HH:mm") : "—"}</p>
              </div>
            </div>
          ))}
          {pushLogs.length === 0 && (
            <p className="py-2 text-sm text-gray-400">{t("admin.notifications.noHistory")}</p>
          )}
        </div>

      </div>
    </AdminConnectedContainer>
  );
}

export default AdminNotificationsPage;
